import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  Renderer2,
} from '@angular/core';
import {
  CmsParagraphComponent,
  CmsService,
  getLastValueSync,
  LanguageService,
} from '@spartacus/core';
import { UserAccountFacade } from '@spartacus/user/account/root';
import { CustomInfluenzaService } from '../custom-influenza.service';
import { BehaviorSubject, first, map, Subscription } from 'rxjs';
import { LocationStripService } from 'src/app/shared/modules/location-strip/location-strip.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { emailPattern, namePattern, phonePattern } from 'src/app/shared';
import { ChartData, ChartOptions } from 'chart.js';
import { OrderItem } from 'src/app/shared/model/order.model';
import { OrderHistoryService } from 'src/app/order-history/order-history.service';
import { UserUtils } from 'src/app/shared/utils/user.utils';
import { Router } from '@angular/router';

@Component({
  selector: 'app-influenza-dashboard',
  templateUrl: './influenza-dashboard.component.html',
  styleUrls: ['./influenza-dashboard.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class InfluenzaDashboardComponent implements OnInit, OnDestroy {
  userData: any = [];
  idofReservation: any;
  selectedRequest$ = new BehaviorSubject<OrderItem>(null);
  chosenReservation: any;
  statusDisplay: any;
  activeSeasons: any;
  cmsResponse: any;
  fluParagraph: any;
  endDate: any;
  todayDate: Date;
  displayActiveSeasons = true;
  startDate: any;
  editClicked = false;
  subscriptions = new Subscription();

  b2bUnitId$ = this.locStrip.userAccountData.pipe(map((res) => res?.uid));
  isPharmacy$ = this.user.get().pipe(map(UserUtils.isPharmacyUser));

  fluParagraphComponent$ = this.cmsData
    .getComponentData<CmsParagraphComponent>('FluParagraphComponent')
    .pipe(map((res) => res?.content || ''));

  editForm = new FormGroup({
    firstName: new FormControl('', [
      Validators.required,
      Validators.pattern(namePattern),
    ]),
    lastName: new FormControl('', [
      Validators.required,
      Validators.pattern(namePattern),
    ]),
    email: new FormControl('', [
      Validators.required,
      Validators.pattern(emailPattern),
    ]),
    phoneNo: new FormControl('', [
      Validators.required,
      Validators.pattern(phonePattern),
    ]),
    fluContactId: new FormControl(''),
    contactMethod: new FormControl('INT'),
  });

  infContacts$ = this.locStrip.userAccountData.pipe(
    map((res) => res?.influenzaContactInfos)
  );

  orderHistory$ = this.influenzaService.allFluOrders;

  chartData: ChartData;

  barChartOptions: ChartOptions<'doughnut'> = {
    responsive: true,
    cutout: '65%',
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
    },
  };

  constructor(
    private readonly cmsData: CmsService,
    private readonly influenzaService: CustomInfluenzaService,
    private readonly user: UserAccountFacade,
    private readonly renderer: Renderer2,
    private readonly ref: ChangeDetectorRef,
    protected locStrip: LocationStripService,
    protected languageService: LanguageService,
    protected orderService: OrderHistoryService,
    protected router: Router
  ) {}

  ngOnInit() {
    this.renderer.listen('document', 'click', (event) => {
      if (event.target.classList.contains('close-banner')) {
        const displayInflunenzaBanner = Array.from(
          document.getElementsByClassName('FluParaMiddleSlottop')
        );
        displayInflunenzaBanner.forEach((element) => {
          element.classList.add('displayNone');
        });
      }
    });

    this.user.get().subscribe((res: any) => {
      if (res) {
        this.userData = res;
      }
    });

    this.influenzaService.getAllFluOrders();
    this.influenzaService.allFluOrders.pipe(first(Boolean)).subscribe((res) => {
      this.setGraph(res[0]?.guid);
    });
  }

  setChartData(data: number[]) {
    this.chartData = {
      labels: ['Shipped', 'Not shipped'],
      datasets: [
        {
          data,
          backgroundColor: ['#7a00e6', '#e4e4e4'],
        },
      ],
    };
  }

  setBarChartOptions(title: string) {
    this.barChartOptions = {
      ...this.barChartOptions,
      plugins: {
        ...this.barChartOptions.plugins,
        title: {
          display: true,
          text: title,
        },
      },
    };
  }

  editContact(fluContactId?: string) {
    this.editClicked = true;
    const infContact = getLastValueSync(this.infContacts$);
    const currentContact = infContact?.find(
      (contact) => contact.fluContactId === fluContactId
    );
    this.editForm.patchValue({
      firstName: currentContact?.firstName,
      lastName: currentContact?.lastName,
      email: currentContact?.email,
      phoneNo: currentContact?.phoneNo,
      fluContactId:
        currentContact?.fluContactId || getLastValueSync(this.b2bUnitId$),
    });
  }

  onSave() {
    const params = {
      ...this.editForm.value,
      preferredLanguage: getLastValueSync(this.languageService.getActive()),
    };
    if (!this.editForm.invalid) {
      this.influenzaService.updateContact(params, this.userData.userName);
      this.influenzaService.updatedInfContact.pipe(first()).subscribe(() => {});
      this.editClicked = false;
    } else {
      this.editForm?.markAllAsTouched();
    }
    this.ref.detectChanges();
  }

  onCancel(): void {
    this.editClicked = false;
  }

  setGraph(orderCode?: string): void {
    this.orderService
      .getOrderDetails(this.userData.userName, orderCode)
      .pipe(first(Boolean))
      .subscribe((order) => {
        const reducedUnconsignedQuantities = order?.entries?.reduce(
          (acc, entry) => {
            return acc + (entry?.quantity || 0);
          },
          0
        );
        const reducedConsignedQuantities = order?.consignments?.reduce(
          (acc, consignment) => {
            return (
              acc +
              consignment?.entries?.reduce(
                (innerAcc, entry) => innerAcc + (entry?.quantity || 0),
                0
              )
            );
          },
          0
        );
        this.setChartData([
          reducedConsignedQuantities,
          reducedUnconsignedQuantities,
        ]);
        this.setBarChartOptions(order?.code);
        this.selectedRequest$.next(order);
      });
  }

  viewOrderDetails(code: string): void {
    this.router.navigate(['influenza/order/', code]);
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
